import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot, Plus, Send, Square, Check, X as XIcon, ChevronDown, ChevronRight,
  Wrench, FileDiff, Info, Trash2, AlertTriangle, Play, HelpCircle, ListChecks, Zap,
  KeyRound, Compass, ClipboardList,
} from "lucide-react";

import {
  fetchAgentStatus, fetchAgentBrief, listSessions, createSession, getSession, deleteSession,
  runTool, fetchChanges, streamMessage, streamApprove, streamEvents, streamCompact, patchSession, cancelRun,
  steerRun, clearSteers,
  type TranscriptItem, type AgentTool, type SessionDetail, type ApprovalMode, type ReasoningEffort,
} from "../api/agent";
import {
  pollSessionStatus, signalLoggedIn, signalSessionDone, signalSessionCancel,
  type SessionStatus,
} from "../api/crawlerOps";
import { explainError } from "../api/client";
import { Spinner } from "../components/Spinner";
import { timeSince } from "../lib/format";

/* ──────────────────────────────────────────────────────────────────────
   /agent — operator agent console (SuperAdmin).

   Three columns:
     · sessions (left)
     · transcript + composer (centre) — streamed text, tool-call cards with
       collapsible args/results, inline Approve/Deny when the runner pauses
     · side panel (right) — Status (provider/policy/tools), Run tool (invoke
       any tool directly, no model needed), Changes (AGENT_CHANGES.md +
       backups), Brief (the system prompt the model sees)
   ────────────────────────────────────────────────────────────────────── */

type Tab = "status" | "tools" | "changes" | "brief";

const RISK_TONE: Record<string, string> = {
  read: "var(--fg-muted)", network: "var(--fg-muted)", log: "var(--fg-muted)", host: "var(--fg-muted)",
  exec: "var(--accent)", write: "var(--accent)",
};

const SUGGESTIONS = [
  "Some newspapers are missing today. Find out what is missing and why, with evidence.",
  "Which publishers have produced nothing for more than 3 days? Diagnose each one.",
  "Check every login session; for any marked failing, prove whether it is really dead.",
  "Give me the daily edition totals for the last two weeks and explain any drop.",
];

export function AgentConsole({ harness = "v1" }: { harness?: "v1" | "v2" } = {}) {
  const v2 = harness === "v2";
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("status");
  const [items, setItems] = useState<TranscriptItem[]>([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamErr, setStreamErr] = useState<string | null>(null);
  // Server-authoritative run state (T17): the composer gates on THIS, not on whether
  // this tab happens to be streaming. idle | running | awaiting_approval
  const [runStatus, setRunStatus] = useState<string>("idle");
  const [pendingCallId, setPendingCallId] = useState<string | null>(null);
  // per-session settings (server persists them; defaults come from /status until a session is loaded)
  const [mode, setMode] = useState<ApprovalMode>("bypass");
  const [effort, setEffort] = useState<ReasoningEffort>("medium");
  const [historyChars, setHistoryChars] = useState<number>(0);
  const [compactAt, setCompactAt] = useState<number>(0);
  // Throughput of the LAST completed response in THIS chat — not a session total.
  const [lastSpeed, setLastSpeed] = useState<{ tokS: number | null; out: number; ms: number } | null>(null);
  const [queued, setQueued] = useState<string[]>([]);
  const [turnId, setTurnId] = useState<string | null>(null);
  const [question, setQuestion] = useState<{ question: string; context?: string; options?: string[] } | null>(null);
  void question;   // rendered from the transcript; state kept so a reload can re-show it
  const [ledger, setLedger] = useState<Array<{ id: string; text: string; status: string; evidence?: string }>>([]);
  const abortRef = useRef<AbortController | null>(null);
  const decideRef = useRef<((callId: string, ok: boolean) => Promise<void>) | null>(null);
  // Synchronous mirror of sessionId: stream callbacks fire between renders,
  // so they must check the CURRENT session, not the one captured at closure time.
  const sessionIdRef = useRef<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const status   = useQuery({ queryKey: ["agent/status", harness], queryFn: () => fetchAgentStatus(harness), refetchInterval: 30_000 });
  const sessions = useQuery({ queryKey: ["agent/sessions"], queryFn: listSessions });
  // Only this console's own sessions — v1 and v2 never show each other's, so the
  // two can be compared side by side without getting tangled.
  const mySessions = useMemo(
    () => (sessions.data ?? []).filter((x) => ((x.harness_version ?? "v1") === harness)),
    [sessions.data, harness]);

  /** Apply one streamed run event to the view. Events for a session that is no longer
   *  on screen are dropped — the server keeps the run regardless. */
  const applyEvent = useCallback((ev: Record<string, unknown>, sid: string) => {
    if (sessionIdRef.current !== sid) return;
    const t = ev.type as string;
    if (t === "turn_start") {
      setRunStatus("running");
      setQuestion(null);
      if (ev.approval_mode === "bypass" || ev.approval_mode === "ask") setMode(ev.approval_mode);
      if (ev.reasoning_effort === "xhigh" || ev.reasoning_effort === "medium" || ev.reasoning_effort === "low") setEffort(ev.reasoning_effort);
      return;
    }
    if (t === "compact_start") { setRunStatus("running"); setItems((cur) => [...cur, { type: "system", text: `compacting ${String(ev.messages)} messages / ${Number(ev.chars).toLocaleString()} chars…` }]); return; }
    if (t === "compact_done") { setHistoryChars(Number(ev.chars_after ?? 0)); setItems((cur) => [...cur, { type: "system", text: `compacted: ${Number(ev.chars_before).toLocaleString()} → ${Number(ev.chars_after).toLocaleString()} chars` }]); return; }
    if (t === "turn_paused") {
      setRunStatus(String(ev.reason) === "needs_input" ? "needs_input" : "awaiting_approval");
      if (String(ev.reason) !== "needs_input") setPendingCallId(String(ev.call_id ?? ""));
      return;
    }
    if (t === "question") {
      setQuestion({ question: String(ev.question ?? ""), context: ev.context ? String(ev.context) : undefined,
                    options: (ev.options as string[] | undefined) ?? [] });
      setItems((cur) => [...cur, { type: "question", question: String(ev.question ?? ""),
                                   context: ev.context ? String(ev.context) : undefined,
                                   options: (ev.options as string[] | undefined) ?? [] }]);
      return;
    }
    if (t === "ledger") { setLedger((ev.items as Array<{ id: string; text: string; status: string; evidence?: string }>) ?? []); return; }
    if (t === "steer_injected") { setQueued([]); setItems((cur) => [...cur, { type: "steer", text: String(ev.text ?? "") }]); return; }
    // The harness's own record of what this turn changed — not the model's summary of it.
    if (t === "change_record") { setItems((cur) => [...cur, { type: "change_record", text: String(ev.text ?? "") }]); return; }
    // This CALL was judged riskier than the tool's declared class (e.g. a host_command
    // that opens a DB connection to write), so it goes through the approval policy.
    if (t === "risk_escalated") {
      setItems((cur) => [...cur, { type: "system",
        text: `${String(ev.name ?? "call")} treated as ${String(ev.risk ?? "write")} (declared ${String(ev.declared ?? "?")}): ${((ev.why as string[] | undefined) ?? []).join("; ")}` }]);
      return;
    }
    if (t === "report_check") {
      if (ev.flagged) setItems((cur) => [...cur, { type: "system",
        text: `report check: possible mismatch with the harness record — ${String(ev.reason ?? "")}; asked it to verify` }]);
      return;
    }
    if (t === "stall") {
      const kind = String(ev.kind ?? "");
      const label = kind === "repeat_call" ? `repeated the same ${String(ev.tool ?? "call")} — blocked, asked it to change approach`
        : kind === "failure_loop" ? `${String(ev.tool ?? "tool")} failed the same way ${String(ev.count ?? "")}× — intervening`
        : kind === "reasoning_loop" ? "reasoning began repeating — round cut"
        : `no progress for ${String(ev.rounds ?? "")} rounds`;
      setItems((cur) => [...cur, { type: "system", text: `stall detected: ${label}` }]);
      return;
    }
    if (t === "recovery") {
      const a = String(ev.action ?? "");
      setItems((cur) => [...cur, { type: "system", text: a === "compact-and-continue"
        ? "recovery: compacting to a handoff note and continuing this turn"
        : "recovery: asked it to stop planning and take one concrete action" }]);
      return;
    }
    if (t === "turn_done") {
      const rs = String(ev.run_status ?? "idle");
      setRunStatus(rs);
      if (rs !== "awaiting_approval") setPendingCallId(null);
      return;
    }
    if (t === "approval_result") setPendingCallId(null);
    setItems((cur) => {
      const next = [...cur];
      const last = next[next.length - 1];
      if (t === "text_delta") {
        if (last && last.type === "assistant" && last.step === (ev.step ?? last.step) && !last.call_id) {
          next[next.length - 1] = { ...last, text: (last.text ?? "") + String(ev.text ?? "") };
        } else next.push({ type: "assistant", text: String(ev.text ?? "") });
      } else if (t === "reasoning_delta") {
        if (last && last.type === "reasoning") {
          next[next.length - 1] = { ...last, text: (last.text ?? "") + String(ev.text ?? ""), chars: (last.chars ?? 0) + String(ev.text ?? "").length };
        } else next.push({ type: "reasoning", text: String(ev.text ?? ""), chars: String(ev.text ?? "").length });
      } else if (t === "nudge") {
        next.push({ type: "system", text: "output budget exhausted — asked for the final report" });
      } else if (t === "failover") {
        next.push({ type: "system", text: `failover → ${String(ev.to ?? "")} (${String(ev.model ?? "")})` });
      } else if (t === "assistant_done") {
        const sp = { tokS: (ev.tok_s as number | null) ?? null, out: Number(ev.out_tokens ?? 0), ms: Number(ev.round_ms ?? 0) };
        setLastSpeed(sp);
        // stamp the speed onto the reply it belongs to, so each response in this chat
        // carries its own number instead of one running total for the session
        if (last && last.type === "assistant") {
          next[next.length - 1] = { ...last, step: Number(ev.step ?? 0) + 1, tok_s: sp.tokS, out_tokens: sp.out, round_ms: sp.ms };
        }
      } else if (t === "tool_call") {
        next.push({ type: "tool_call", call_id: String(ev.call_id), name: String(ev.name), arguments: (ev.arguments as Record<string, unknown>) ?? {} });
      } else if (t === "approval_required") {
        next.push({ type: "approval_required", call_id: String(ev.call_id), name: String(ev.name), arguments: (ev.arguments as Record<string, unknown>) ?? {}, risk: String(ev.risk ?? ""),
                    declared_risk: ev.declared_risk ? String(ev.declared_risk) : undefined, why: (ev.why as string[] | undefined) ?? undefined });
      } else if (t === "approval_result") {
        next.push({ type: "approval_result", call_id: String(ev.call_id), approved: Boolean(ev.approved), text: ev.reason ? String(ev.reason) : undefined, by: ev.by ? String(ev.by) : undefined });
      } else if (t === "tool_result") {
        next.push({ type: "tool_result", call_id: String(ev.call_id), name: String(ev.name), ok: Boolean(ev.ok), result: String(ev.result ?? ""), duration_ms: Number(ev.duration_ms ?? 0) });
      } else if (t === "error") {
        next.push({ type: "error", text: String(ev.message ?? "error") });
      }
      return next;
    });
  }, []);

  /** Ask the server where the session really stands (after any stream ends). */
  const settle = useCallback(async (sid: string): Promise<SessionDetail | null> => {
    let s: SessionDetail | null = null;
    try {
      s = await getSession(sid);
      if (sessionIdRef.current !== sid) return null;
      const rs = s.run_status ?? "idle";
      setRunStatus(rs);
      setPendingCallId(rs === "awaiting_approval" ? ((s.pending_call as { call_id?: string } | null)?.call_id ?? null) : null);
      if (typeof s.history_chars === "number") setHistoryChars(s.history_chars);
      if (typeof s.compact_at_chars === "number") setCompactAt(s.compact_at_chars);
      setQueued((s.queued_steers as string[] | undefined) ?? []);
      setTurnId((s.turn_id as string | null | undefined) ?? null);
      setQuestion(rs === "needs_input" && s.pending_question
        ? { question: String((s.pending_question as { question?: string }).question ?? ""),
            context: (s.pending_question as { context?: string }).context,
            options: ((s.pending_question as { options?: string[] }).options) ?? [] }
        : null);
      setLedger(((s.ledger as Array<{ id: string; text: string; status: string; evidence?: string }> | undefined) ?? []));
      // Keep the toggle showing the SERVER's effective mode/effort so it can never drift from reality.
      const em = s.effective_approval_mode; if (em === "bypass" || em === "ask") setMode(em);
      const ee = s.effective_reasoning_effort; if (ee === "xhigh" || ee === "medium" || ee === "low") setEffort(ee);
      // Run over → the persisted transcript is the truth (fills anything a dropped stream
      // missed, carries the saved reasoning text, no duplicates).
      if (!s.running) setItems(s.transcript as unknown as TranscriptItem[]);
    } catch { /* ignore */ }
    void qc.invalidateQueries({ queryKey: ["agent/sessions"] });
    void qc.invalidateQueries({ queryKey: ["agent/changes"] });
    return s;
  }, [qc]);

  /** Re-attach to a run already in progress on the server (reload, reconnect, other tab). */
  const attach = useCallback(async (sid: string) => {
    if (abortRef.current) return;
    const ac = new AbortController(); abortRef.current = ac;
    setStreaming(true); setStreamErr(null);
    try {
      // catch up from disk first so nothing between the drop and now is missing from the view
      try { const s0 = await getSession(sid); if (sessionIdRef.current === sid) setItems(s0.transcript as unknown as TranscriptItem[]); } catch { /* ignore */ }
      await streamEvents(sid, (ev) => applyEvent(ev, sid), ac.signal);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) setStreamErr(explainError(e));
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
      setStreaming(false);
      const s = await settle(sid);
      const userStopped = ac.signal.aborted;
      if (!userStopped && s && s.running && s.run_status === "running" && sessionIdRef.current === sid) {
        await new Promise((r) => setTimeout(r, 1000));
        if (!abortRef.current && sessionIdRef.current === sid) { setStreamErr(null); void attach(sid); }
      }
    }
  }, [applyEvent, settle]);

  const load = useCallback(async (id: string) => {
    abortRef.current?.abort();          // stop WATCHING the previous session; its run continues server-side
    abortRef.current = null;
    sessionIdRef.current = id;
    const s: SessionDetail = await getSession(id);
    if (sessionIdRef.current !== id) return;
    setSessionId(id);
    setItems(s.transcript as unknown as TranscriptItem[]);
    setStreamErr(null);
    const rs = s.run_status ?? "idle";
    setRunStatus(rs);
    setPendingCallId(rs === "awaiting_approval" ? ((s.pending_call as { call_id?: string } | null)?.call_id ?? null) : null);
    const em = s.effective_approval_mode; if (em === "bypass" || em === "ask") setMode(em);
    const ee = s.effective_reasoning_effort; if (ee === "xhigh" || ee === "medium" || ee === "low") setEffort(ee);
    if (typeof s.history_chars === "number") setHistoryChars(s.history_chars);
    if (typeof s.compact_at_chars === "number") setCompactAt(s.compact_at_chars);
    setQueued((s.queued_steers as string[] | undefined) ?? []);
    setTurnId((s.turn_id as string | null | undefined) ?? null);
    setLedger(((s.ledger as Array<{ id: string; text: string; status: string; evidence?: string }> | undefined) ?? []));
    setQuestion(rs === "needs_input" && s.pending_question
      ? { question: String((s.pending_question as { question?: string }).question ?? ""),
          context: (s.pending_question as { context?: string }).context,
          options: ((s.pending_question as { options?: string[] }).options) ?? [] }
      : null);
    setLastSpeed(null);
    if (s.running) void attach(id);
  }, [attach]);

  const create = useMutation({
    mutationFn: () => createSession(undefined, harness),
    onSuccess: async (s) => { await qc.invalidateQueries({ queryKey: ["agent/sessions"] }); await load(s.id); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteSession(id),
    onSuccess: async () => { abortRef.current?.abort(); abortRef.current = null; sessionIdRef.current = null; setSessionId(null); setItems([]); setRunStatus("idle"); setPendingCallId(null); await qc.invalidateQueries({ queryKey: ["agent/sessions"] }); },
  });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items, streaming]);

  const busy: boolean = runStatus === "running" || runStatus === "awaiting_approval";
  const needsAnswer: boolean = runStatus === "needs_input";
  // In v2 the composer stays OPEN while the agent works: what you type is steered into
  // the running turn instead of being blocked until it finishes.
  const canSteer = v2 && runStatus === "running";

  /** Persist a settings change immediately. If you switch to Bypass while an approval card is
   *  pending, that means "just do it" — so approve the pending action and continue. */
  const changeMode = useCallback((m: ApprovalMode) => {
    setMode(m);
    if (!sessionId) return;
    void patchSession(sessionId, { approval_mode: m })
      .then(() => { if (m === "bypass" && runStatus === "awaiting_approval" && pendingCallId) void decideRef.current?.(pendingCallId, true); })
      .catch((e) => setStreamErr(explainError(e)));
  }, [sessionId, runStatus, pendingCallId]);
  const changeEffort = useCallback((e: ReasoningEffort) => { setEffort(e); if (sessionId) void patchSession(sessionId, { reasoning_effort: e }).catch((err) => setStreamErr(explainError(err))); }, [sessionId]);

  /** /compact — summarise the history into a handoff note (also reachable by typing "/compact"). */
  const runCompact = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid || streaming || busy) return;
    setDraft(""); setStreamErr(null); setStreaming(true); setRunStatus("running");
    const ac = new AbortController(); abortRef.current = ac;
    try { await streamCompact(sid, (ev) => applyEvent(ev, sid), ac.signal); }
    catch (e) { if (!(e instanceof DOMException && e.name === "AbortError")) setStreamErr(explainError(e)); }
    finally { if (abortRef.current === ac) abortRef.current = null; setStreaming(false); await settle(sid); }
  }, [streaming, busy, applyEvent, settle]);

  /** Stop the running turn on the server (pending tool calls become NOT EXECUTED). */
  const stopRun = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    if (!confirm("Stop the running turn? Any tool call still pending will be recorded as NOT EXECUTED.")) return;
    try { await cancelRun(sid); } catch (e) { setStreamErr(explainError(e)); }
    abortRef.current?.abort(); abortRef.current = null;
    await settle(sid);
  }, [settle]);

  const send = useCallback(async (text: string) => {
    if (!text.trim()) return;
    // v2: a turn is running → steer it instead of refusing the message
    if (canSteer && sessionIdRef.current) {
      const sid0 = sessionIdRef.current;
      setDraft("");
      setQueued((q) => [...q, text]);
      try { await steerRun(sid0, text, turnId); }
      catch (e) { setQueued((q) => q.filter((x) => x !== text)); setStreamErr(explainError(e)); }
      return;
    }
    if (streaming || busy) return;
    if (text.trim() === "/compact") { void runCompact(); return; }
    let sid = sessionId;
    if (!sid) {
      const s = await createSession(undefined, harness);
      sid = s.id; sessionIdRef.current = sid; setSessionId(sid);
      void qc.invalidateQueries({ queryKey: ["agent/sessions"] });
    }
    const sidNow = sid;
    setDraft("");
    setStreamErr(null);
    setItems((cur) => [...cur, { type: "user", text, at: new Date().toISOString() }]);
    setStreaming(true); setRunStatus("running");
    const ac = new AbortController(); abortRef.current = ac;
    try {
      await streamMessage(sidNow, text, (ev) => applyEvent(ev, sidNow), ac.signal, { approval_mode: mode, reasoning_effort: effort });
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) setStreamErr(explainError(e));
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
      setStreaming(false);
      const s = await settle(sidNow);
      // The stream ended but the run did not (network blip, proxy, tab throttling, clean early
      // close) — re-attach automatically. Only a deliberate Stop is left alone.
      if (!ac.signal.aborted && s && s.running && s.run_status === "running" && sessionIdRef.current === sidNow) {
        setStreamErr(null); void attach(sidNow);
      }
    }
  }, [sessionId, streaming, busy, qc, applyEvent, settle, attach, mode, effort, runCompact, canSteer, turnId, harness]);

  /** Approve / deny the parked call; the server persists the decision and resumes the run. */
  const decide = useCallback(async (callId: string, ok: boolean) => {
    const sid = sessionIdRef.current;
    if (!sid || streaming) return;
    setStreamErr(null); setPendingCallId(null); setRunStatus("running"); setStreaming(true);
    const ac = new AbortController(); abortRef.current = ac;
    try {
      await streamApprove(sid, callId, ok, (ev) => applyEvent(ev, sid), ac.signal);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) setStreamErr(explainError(e));
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
      setStreaming(false);
      const s = await settle(sid);
      if (!ac.signal.aborted && s && s.running && s.run_status === "running" && sessionIdRef.current === sid) {
        setStreamErr(null); void attach(sid);
      }
    }
  }, [streaming, applyEvent, settle, attach]);
  decideRef.current = decide;

  const configured = status.data?.settings.configured ?? false;
  useEffect(() => {
    const d = status.data?.defaults;
    if (!d || sessionId) return;
    if (d.approval_mode === "bypass" || d.approval_mode === "ask") setMode(d.approval_mode);
    if (d.reasoning_effort === "xhigh" || d.reasoning_effort === "medium" || d.reasoning_effort === "low") setEffort(d.reasoning_effort);
    setCompactAt(d.compact_at_chars);
  }, [status.data, sessionId]);

  return (
    <div className="flex flex-col gap-3 animate-fade-up h-[calc(100vh-7.5rem)]">
      <header className="flex items-end justify-between flex-wrap gap-2 pb-2 border-b shrink-0" style={{ borderColor: "var(--rule)" }}>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">
            Operations · Agent{v2 ? " v2" : ""}
          </div>
          <h1 className="font-display text-[26px] leading-tight flex items-center gap-2">
            <Bot size={22} /> Operator agent{v2 ? " v2" : ""}
            {v2 && (
              <span className="np-chip font-mono text-[10px]" title="Progress-based stall detection, recovery ladder, mid-task steering, ask-the-operator"
                    style={{ background: "var(--accent-soft)", color: "var(--accent)", borderColor: "transparent" }}>
                steering · stall recovery
              </span>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-3 font-mono text-[11px]">
          {status.data && (
            <span className="np-chip" style={{ background: configured ? "var(--bg-inset)" : "var(--accent-soft)", color: configured ? "var(--fg-muted)" : "var(--accent)", borderColor: "transparent" }}>
              {configured ? `${status.data.settings.provider} · ${status.data.settings.model}` : "no model endpoint configured"}
            </span>
          )}
          {status.data && (
            <span className="text-[var(--fg-faint)]">
              edits {status.data.settings.allow_edits ? "on" : "off"} · sql writes {status.data.settings.allow_sql_writes ? "on" : "off"} · approval {status.data.settings.approval_policy}
            </span>
          )}
          {sessionId && (
            <span className="np-chip font-mono text-[11px]" title="Throughput of the last response in THIS chat (not a session total). Each reply also shows its own speed."
                  style={{ background: "var(--bg-inset)", color: "var(--fg)", borderColor: "transparent" }}>
              ⚡ {lastSpeed?.tokS != null ? `${lastSpeed.tokS} tok/s` : (busy ? "measuring…" : "— tok/s")}
              <span className="text-[var(--fg-faint)]"> last reply</span>
            </span>
          )}
        </div>
      </header>

      <div className="grid grid-cols-[220px,minmax(0,1fr),380px] gap-3 flex-1 min-h-0 min-w-0">
        {/* ── sessions ─────────────────────────────────────────── */}
        <aside className="np-card flex flex-col min-h-0">
          <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--rule)" }}>
            <span className="font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]">Sessions</span>
            <button className="np-btn-ghost p-1" onClick={() => create.mutate()} title="New session"><Plus size={13} /></button>
          </div>
          <ul className="overflow-y-auto flex-1">
            {mySessions.map((s) => (
              <li key={s.id}>
                <button onClick={() => void load(s.id)}
                        className={`w-full text-left px-3 py-2 border-b hover:bg-[var(--bg-inset)] ${s.id === sessionId ? "bg-[var(--bg-inset)]" : ""}`}
                        style={{ borderColor: "var(--rule)" }}>
                  <div className="font-sans text-[12px] truncate">{s.title}</div>
                  <div className="font-mono text-[9px] text-[var(--fg-faint)]">
                    {s.turns} turns · {s.tool_calls} tools · {timeSince(s.updated_at)}
                    {(s.run_status ?? s.status) === "running" && <span className="text-[var(--accent)]"> · running</span>}
                    {s.run_status === "awaiting_approval" && <span className="text-[var(--accent)]"> · needs approval</span>}
                    {s.approval_mode === "bypass" && <span> · bypass</span>}
                    {(s.compactions ?? 0) > 0 && <span> · compacted×{s.compactions}</span>}
                  </div>
                </button>
              </li>
            ))}
            {sessions.data && mySessions.length === 0 && (
              <li className="px-3 py-4 font-sans text-[12px] text-[var(--fg-muted)]">No {v2 ? "v2 " : ""}sessions yet.</li>
            )}
          </ul>
        </aside>

        {/* ── transcript ───────────────────────────────────────── */}
        <section className="np-card flex flex-col min-h-0 min-w-0">
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 flex flex-col gap-2">
            {items.length === 0 && (
              <div className="my-auto text-center">
                <div className="font-display text-[18px] mb-1">Ask the on-call agent</div>
                <div className="font-sans text-[12px] text-[var(--fg-muted)] mb-4 max-w-[520px] mx-auto">
                  It reads the crawler DB, the code and the knowledge base, probes publishers the way the runner does,
                  and — when edits are enabled and you approve — fixes and verifies.
                </div>
                <div className="flex flex-col gap-1 items-center">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => void send(s)} disabled={!configured}
                            className="np-btn-ghost font-sans text-[12px] text-left px-3 py-1.5 border max-w-[560px] w-full"
                            style={{ borderColor: "var(--rule)" }}>{s}</button>
                  ))}
                </div>
                {!configured && (
                  <div className="np-banner mt-4 inline-flex items-center gap-2 text-left">
                    <AlertTriangle size={13} className="text-[var(--accent)] shrink-0" />
                    <span className="font-sans text-[12px]">
                      No model endpoint yet — set <code>AGENT_API_BASE</code> / <code>AGENT_MODEL</code> on the API host.
                      Tools still work from the <b>Run tool</b> tab.
                    </span>
                  </div>
                )}
              </div>
            )}
            {items.map((it, i) => (
              <TranscriptRow key={i} it={it} live={streaming && i === items.length - 1} pendingCallId={pendingCallId}
                             onDecide={decide} onAnswer={(t) => void send(t)}
                             answerable={needsAnswer && i === items.length - 1} />
            ))}
            {(streaming || busy || needsAnswer) && (
              <div className="font-mono text-[10px] text-[var(--fg-faint)] flex items-center gap-2">
                <Spinner label="" />
                {runStatus === "awaiting_approval" ? "paused — waiting for your approval above"
                  : needsAnswer ? "waiting for your answer above"
                  : streaming ? (lastSpeed?.tokS != null ? `working… last reply ${lastSpeed.tokS} tok/s` : "working…") : "running on the server — this tab is not attached"}
                {!streaming && runStatus === "running" && sessionId && (
                  <button type="button" className="underline" onClick={() => void attach(sessionId)}>re-attach</button>
                )}
              </div>
            )}
            {streamErr && <div className="np-error font-mono text-[11px]">{streamErr}</div>}
            <div ref={bottomRef} />
          </div>
          <div className="border-t px-2 pt-1.5 flex items-center gap-2 flex-wrap font-mono text-[10px]" style={{ borderColor: "var(--rule)" }}>
            <span className="text-[var(--fg-faint)] uppercase tracking-folio">mode</span>
            <div className="flex border" style={{ borderColor: "var(--rule)" }}>
              {(["bypass", "ask"] as const).map((m) => (
                <button key={m} type="button" onClick={() => changeMode(m)} title={m === "bypass" ? "Bypass permission — exec/write tools run without asking" : "Needs approval — pause and ask before every exec/write tool"}
                        className="px-2 py-0.5" style={{ background: mode === m ? "var(--accent-soft)" : "transparent", color: mode === m ? "var(--accent)" : "var(--fg-muted)" }}>
                  {m === "bypass" ? "Bypass permission" : "Needs approval"}
                </button>
              ))}
            </div>
            <span className="text-[var(--fg-faint)] uppercase tracking-folio ml-2">effort</span>
            <select value={effort} onChange={(e) => changeEffort(e.target.value as ReasoningEffort)} className="np-input py-0.5 px-1 font-mono text-[10px]" title="Reasoning effort sent to the model (xhigh is the model's own default)">
              <option value="low">low</option><option value="medium">medium</option><option value="xhigh">xhigh</option>
            </select>
            {ledger.length > 0 && (
              <span className="text-[var(--fg-faint)] flex items-center gap-1" title={ledger.map((l) => `[${l.status}] ${l.text}`).join("\n")}>
                <ListChecks size={11} /> {ledger.filter((l) => l.status === "done").length}/{ledger.length}
              </span>
            )}
            <span className="ml-auto text-[var(--fg-faint)]" title="Speed of the last reply in this chat; history size for this session">
              {lastSpeed?.tokS != null ? `⚡ ${lastSpeed.tokS} tok/s · ` : ""}
              history {historyChars.toLocaleString()} chars{compactAt > 0 ? ` · auto-compact ${(compactAt/1000).toFixed(0)}k` : ""}
            </span>
            <button type="button" className="np-btn-ghost px-2 py-0.5 border" style={{ borderColor: "var(--rule)" }} disabled={!sessionId || streaming || busy}
                    onClick={() => void runCompact()} title="Summarise the history into a handoff note so the model reads less on every call (same as typing /compact)">/compact</button>
            {busy && (
              <button type="button" className="np-btn-ghost px-2 py-0.5 border" style={{ borderColor: "var(--accent)", color: "var(--accent)" }} onClick={() => void stopRun()} title="Stop the running turn on the server">Stop run</button>
            )}
          </div>
          {queued.length > 0 && (
            <div className="px-2 pt-1 flex flex-wrap items-center gap-1 font-mono text-[10px]">
              <span className="text-[var(--fg-faint)] uppercase tracking-folio">queued</span>
              {queued.map((q, i) => (
                <span key={i} className="np-chip flex items-center gap-1" style={{ background: "var(--bg-inset)", borderColor: "var(--rule)" }}>
                  {q.length > 48 ? q.slice(0, 48) + "…" : q}
                </span>
              ))}
              <button type="button" className="np-btn-ghost px-1" title="Take back everything still queued (it has not been shown to the agent yet)"
                      onClick={() => { const sid = sessionIdRef.current; if (sid) void clearSteers(sid).then(() => setQueued([])).catch(() => undefined); }}>
                <XIcon size={11} />
              </button>
            </div>
          )}
          <form className="p-2 flex items-end gap-2"
                onSubmit={(e) => { e.preventDefault(); void send(draft); }}>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
                      placeholder={!configured ? "Configure the model endpoint to chat; tools run from the side panel."
                        : runStatus === "awaiting_approval" ? "Waiting for your approval above…"
                        : needsAnswer ? "The agent asked you a question above — answer it here to continue."
                        : canSteer ? "The agent is working — type to steer it. Your message goes in after the current step."
                        : runStatus === "running" ? "The agent is working — it keeps running even if you close this tab."
                        : "Describe the problem, or ask for a check…  (type /compact to shrink a long session)"}
                      disabled={!canSteer && (streaming || busy) && !needsAnswer}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(draft); } }}
                      className="np-input flex-1 font-sans text-[13px] resize-none" />
            {streaming && !canSteer ? (
              <button type="button" className="np-btn-ghost p-2" onClick={() => abortRef.current?.abort()} title="Stop watching (the run continues on the server)"><Square size={14} /></button>
            ) : (
              <button type="submit" className="np-btn p-2"
                      disabled={!draft.trim() || !configured || (busy && !canSteer && !needsAnswer)}
                      title={canSteer ? "Steer the running turn" : "Send"}><Send size={14} /></button>
            )}
            {sessionId && !streaming && !busy && (
              <button type="button" className="np-btn-ghost p-2" title="Delete session" onClick={() => { if (confirm("Delete this session?")) remove.mutate(sessionId); }}><Trash2 size={14} /></button>
            )}
          </form>
        </section>

        {/* ── side panel ───────────────────────────────────────── */}
        <aside className="np-card flex flex-col min-h-0 min-w-0">
          <nav className="flex border-b" style={{ borderColor: "var(--rule)" }}>
            {([["status", "Status", Info], ["tools", "Run tool", Wrench], ["changes", "Changes", FileDiff], ["brief", "Brief", Bot]] as const).map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)}
                      className="flex-1 px-2 py-2 font-mono text-[10px] uppercase tracking-folio flex items-center justify-center gap-1"
                      style={{ color: tab === k ? "var(--fg)" : "var(--fg-muted)", borderBottom: tab === k ? "2px solid var(--accent)" : "2px solid transparent" }}>
                <Icon size={11} /> {label}
              </button>
            ))}
          </nav>
          <div className="flex-1 overflow-y-auto p-3">
            {tab === "status"  && <StatusPanel harness={harness} />}
            {tab === "tools"   && <ToolsPanel tools={status.data?.tools ?? []} />}
            {tab === "changes" && <ChangesPanel />}
            {tab === "brief"   && <BriefPanel harness={harness} />}
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ── transcript rows (also used by /agent3, AgentConsole3.tsx) ────────── */
export function TranscriptRow({ it, live, pendingCallId, onDecide, onAnswer, answerable }: { it: TranscriptItem; live: boolean; pendingCallId: string | null; onDecide: (callId: string, ok: boolean) => void; onAnswer?: (text: string) => void; answerable?: boolean }) {
  // null = user hasn't toggled; a live (streaming) thinking row shows its text as it arrives
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? (live && it.type === "reasoning");
  const setOpen = (f: (o: boolean) => boolean) => setToggled(f(open));
  if (it.type === "user") {
    return <div className="self-end max-w-[85%] px-3 py-2 font-sans text-[13px] whitespace-pre-wrap break-words" style={{ background: "var(--bg-inset)", borderLeft: "3px solid var(--primary)" }}>{it.text}</div>;
  }
  if (it.type === "assistant") {
    return (
      <div className="max-w-[92%] px-3 py-2">
        <div className="font-sans text-[13px] whitespace-pre-wrap break-words leading-relaxed">{it.text}</div>
        {it.tok_s != null && (
          <div className="font-mono text-[9px] text-[var(--fg-faint)] mt-1 flex items-center gap-1"
               title="Throughput of this reply — output tokens ÷ time for this round">
            <Zap size={9} /> {it.tok_s} tok/s
            {it.out_tokens ? ` · ${it.out_tokens.toLocaleString()} tokens` : ""}
            {it.round_ms ? ` · ${(it.round_ms / 1000).toFixed(1)}s` : ""}
          </div>
        )}
      </div>
    );
  }
  if (it.type === "question") {
    return (
      <div className="px-3 py-2 border font-sans text-[13px]" style={{ borderColor: "var(--accent)", background: "var(--accent-soft)" }}>
        <div className="flex items-center gap-2 mb-1 font-mono text-[11px]">
          <HelpCircle size={12} className="text-[var(--accent)]" />
          <span className="text-[var(--fg)]">The agent needs your answer</span>
        </div>
        <div className="whitespace-pre-wrap break-words">{it.question}</div>
        {it.context && <div className="font-sans text-[12px] text-[var(--fg-muted)] mt-1 whitespace-pre-wrap">{it.context}</div>}
        {!!(it.options && it.options.length) && (
          <div className="flex flex-col gap-1 mt-2">
            {it.options.map((o, i) => (
              <button key={i} type="button" disabled={!answerable}
                      onClick={() => onAnswer?.(o)}
                      title={answerable ? "Answer with this option" : "This question has already been answered"}
                      className="text-left px-2 py-1.5 border font-sans text-[12px] flex items-start gap-2 disabled:opacity-55"
                      style={{ borderColor: "var(--rule)", background: "var(--bg)" }}>
                <span className="font-mono text-[10px] text-[var(--fg-faint)] mt-[2px]">{i + 1}</span>
                <span>{o}</span>
              </button>
            ))}
          </div>
        )}
        <div className="font-mono text-[10px] text-[var(--fg-faint)] mt-1">
          {answerable ? (it.options && it.options.length ? "Click an option, or type your own answer below." : "Type your answer below.")
                      : "Answered."}
          {" "}The turn continues with everything it has already done.
        </div>
      </div>
    );
  }
  if (it.type === "steer") {
    return (
      <div className="self-end max-w-[85%] px-3 py-1.5 font-sans text-[12px] whitespace-pre-wrap break-words"
           style={{ background: "var(--bg-inset)", borderLeft: "3px solid var(--accent)" }}>
        <div className="font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)] mb-0.5">sent while working</div>
        {it.text}
      </div>
    );
  }
  if (it.type === "reasoning") {
    const n = it.chars ?? (it.text ?? "").length;
    return (
      <div className="font-mono text-[10px] text-[var(--fg-faint)] px-3">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1">
          {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />} thinking · {n.toLocaleString()} chars
        </button>
        {open && it.text && <pre className="mt-1 whitespace-pre-wrap break-words max-h-[240px] overflow-auto opacity-70">{it.text}</pre>}
        {open && !it.text && <div className="mt-1 opacity-60">(thinking text not kept for this round)</div>}
      </div>
    );
  }
  if (it.type === "system") {
    return <div className="font-mono text-[10px] text-[var(--accent)] px-3">· {it.text}</div>;
  }
  if (it.type === "error") {
    return <div className="np-error font-mono text-[11px]">{it.text}</div>;
  }
  if (it.type === "tool_call") {
    return (
      <div className="font-mono text-[11px] px-3 py-1.5 border min-w-0" style={{ borderColor: "var(--rule)" }}>
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 w-full min-w-0 text-left">
          {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          <Wrench size={11} className="text-[var(--fg-faint)]" />
          <span className="text-[var(--fg)]">{it.name}</span>
          <span className="text-[var(--fg-faint)] truncate min-w-0">{summarizeArgs(it.arguments)}</span>
        </button>
        {open && <pre className="mt-1 text-[10px] whitespace-pre-wrap text-[var(--fg-muted)] max-h-[200px] overflow-auto">{JSON.stringify(it.arguments, null, 2)}</pre>}
      </div>
    );
  }
  if (it.type === "tool_result" && it.name === "session_capture_start") {
    // The agent started a capture. Instead of leaving the operator with a bare
    // noVNC link and no way to finish it, show the SAME browser and the SAME
    // buttons the Sessions page has, right here in the conversation.
    let cid: number | null = null;
    let novnc: string | null = null;
    let mode = "login";
    try {
      const r = JSON.parse(it.result ?? "{}") as { channel_id?: number; novnc_url?: string; mode?: string };
      cid = typeof r.channel_id === "number" ? r.channel_id : null;
      novnc = r.novnc_url ?? null;
      mode = r.mode ?? "login";
    } catch { /* fall through to the plain result row */ }
    if (cid !== null) return <CaptureCard channelId={cid} initialNovnc={novnc} mode={mode} />;
  }
  if (it.type === "tool_result") {
    return (
      <div className="font-mono text-[11px] px-3 py-1.5 border-l-2 ml-3 min-w-0" style={{ borderColor: it.ok ? "var(--rule-strong)" : "var(--accent)" }}>
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 w-full min-w-0 text-left">
          {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          <span className="text-[var(--fg-faint)] truncate min-w-0">↳ {it.name} · {it.ok ? "ok" : "error"} · {it.duration_ms} ms · {(it.result ?? "").length.toLocaleString()} chars</span>
        </button>
        {open && <pre className="mt-1 text-[10px] whitespace-pre-wrap text-[var(--fg-muted)] max-h-[360px] overflow-auto">{it.result}</pre>}
      </div>
    );
  }
  if (it.type === "approval_required") {
    return <ApprovalCard it={it} active={!!it.call_id && it.call_id === pendingCallId} onDecide={onDecide} />;
  }
  if (it.type === "approval_result") {
    const label = it.approved ? "✓ approved"
      : it.by === "write_gate" ? "✗ refused by the harness — the last message did not authorise changes"
      : "✗ denied";
    return <div className="font-mono text-[10px] text-[var(--fg-faint)] ml-3">{label}{it.text ? ` — ${it.text}` : ""}</div>;
  }
  // The harness's own record of what changed, next to the claims about it. The model
  // does not write this block, which is the whole point of it.
  if (it.type === "change_record") {
    return (
      <div className="px-3 py-2 border font-sans text-[12px]" style={{ borderColor: "var(--rule)", background: "var(--bg-inset)" }}>
        <div className="flex items-center gap-2 mb-1 font-mono text-[10px] text-[var(--fg-muted)]">
          <ClipboardList size={11} /> recorded by the harness — not written by the agent
        </div>
        <pre className="whitespace-pre-wrap break-words text-[11px] leading-relaxed max-h-[420px] overflow-auto">{(it.text ?? "").replace(/^\n?---\n?### What the harness recorded this turn\n?/, "")}</pre>
      </div>
    );
  }
  return null;
}

function ApprovalCard({ it, active, onDecide }: { it: TranscriptItem; active: boolean; onDecide: (callId: string, ok: boolean) => void }) {
  return (
    <div className="px-3 py-2 border font-mono text-[11px]" style={{ borderColor: "var(--accent)", background: "var(--accent-soft)" }}>
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={12} className="text-[var(--accent)]" />
        <span className="text-[var(--fg)]">Agent wants to run <b>{it.name}</b> <span className="text-[var(--fg-faint)]">({it.risk})</span></span>
        {it.always_asks && <span className="np-chip font-mono text-[9px]" style={{ background: "var(--accent)", color: "var(--bg)", borderColor: "transparent" }}>always asks</span>}
        {it.declared_risk && it.declared_risk !== it.risk && <span className="np-chip font-mono text-[9px]" style={{ background: "var(--accent)", color: "var(--bg)", borderColor: "transparent" }}>escalated from {it.declared_risk}</span>}
      </div>
      {!!(it.why && it.why.length) && (
        <div className="font-mono text-[10px] text-[var(--fg-muted)] mb-1">
          Asking because this call can change state: {it.why.join("; ")}
        </div>
      )}
      <pre className="text-[10px] whitespace-pre-wrap break-all text-[var(--fg-muted)] max-h-[220px] overflow-auto">{JSON.stringify(it.arguments, null, 2)}</pre>
      {active && it.call_id && (
        <div className="flex gap-2 mt-2">
          <button className="np-btn flex items-center gap-1" onClick={() => onDecide(it.call_id!, true)}><Check size={12} /> Approve</button>
          <button className="np-btn-ghost flex items-center gap-1 border" style={{ borderColor: "var(--rule)" }} onClick={() => onDecide(it.call_id!, false)}><XIcon size={12} /> Deny</button>
        </div>
      )}
    </div>
  );
}

function summarizeArgs(a?: Record<string, unknown>): string {
  if (!a) return "";
  return Object.entries(a).map(([k, v]) => `${k}=${typeof v === "string" ? JSON.stringify(v.length > 40 ? v.slice(0, 40) + "…" : v) : JSON.stringify(v)}`).join(" ");
}

/* ── capture card ──────────────────────────────────────────────────────
   The agent can start a capture but cannot drive the browser — the operator
   must. Previously it handed over a noVNC URL and the operator opened it in a
   bare tab with no "I'm logged in" / "Finish & capture" buttons (those live on
   the Sessions page), so they had to tell the agent in chat to signal for them.
   This is the same browser and the same controls, inline in the conversation.
   It calls the existing /api/crawler endpoints — no new backend. */
function CaptureCard({ channelId, initialNovnc, mode }: { channelId: number; initialNovnc: string | null; mode: string }) {
  const [status, setStatus] = useState<SessionStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try { const s = await pollSessionStatus(channelId); if (!stop) setStatus(s); }
      catch (e) { if (!stop) setErr(explainError(e)); }
    };
    void tick();
    const t = setInterval(() => {
      const p = status?.status;
      if (p && ["done", "failed", "cancelled", "timeout"].includes(p)) return;
      void tick();
    }, 2500);
    return () => { stop = true; clearInterval(t); };
  }, [channelId, status?.status]);

  const phase = status?.status ?? "starting";
  const novnc = status?.novnc_url ?? initialNovnc;
  const terminal = ["done", "failed", "cancelled", "timeout"].includes(phase);
  const showBrowser = !!novnc && (phase === "waiting_for_login" || phase === "waiting_for_navigate");
  const act = async (fn: (c: number) => Promise<SessionStatus>) => {
    setErr(null);
    try { setStatus(await fn(channelId)); } catch (e) { setErr(explainError(e)); }
  };

  return (
    <div className="border my-1" style={{ borderColor: terminal ? "var(--rule)" : "var(--accent)" }}>
      <div className="px-3 py-2 flex items-center gap-2 font-mono text-[11px]"
           style={{ background: terminal ? "var(--bg-inset)" : "var(--accent-soft)" }}>
        {mode === "login" ? <KeyRound size={12} className="text-[var(--accent)]" />
                          : <Compass size={12} className="text-[var(--accent)]" />}
        <span className="text-[var(--fg)]">
          Session capture · channel {channelId} · {mode === "login" ? "log in, then click through" : "click through"}
        </span>
        <span className="np-chip" style={{ background: "var(--bg)", borderColor: "var(--rule)" }}>
          {phase.replace(/_/g, " ")}
        </span>
        {typeof status?.captured === "number" && (
          <span className="text-[var(--fg-faint)]">{status.captured} requests</span>
        )}
        <button type="button" className="np-btn-ghost px-1 ml-auto" onClick={() => setOpen((o) => !o)}>
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
      </div>

      {open && (
        <>
          <div className="px-3 py-1.5 font-sans text-[12px] text-[var(--fg-muted)]"
               style={{ borderTop: "1px solid var(--rule)" }}>
            {status?.message ?? "starting the remote browser…"}
          </div>
          {showBrowser ? (
            <iframe title="Capture browser" src={novnc!} className="w-full bg-black"
                    style={{ border: "none", height: "min(62vh, 620px)" }}
                    allow="clipboard-read; clipboard-write" />
          ) : !terminal ? (
            <div className="px-3 py-6 text-center font-sans text-[12px] text-[var(--fg-muted)]">
              {phase === "starting" && "Spinning up the remote browser…"}
              {phase === "capturing" && "Capturing the full session…"}
              {!["starting", "capturing"].includes(phase) && "Preparing…"}
            </div>
          ) : null}
          {err && <div className="np-error font-mono text-[11px] m-2">{err}</div>}
          <div className="px-3 py-2 flex items-center gap-2 flex-wrap"
               style={{ borderTop: "1px solid var(--rule)" }}>
            {phase === "waiting_for_login" && (
              <button type="button" className="np-btn flex items-center gap-1"
                      onClick={() => void act(signalLoggedIn)}
                      title="You have signed in — continue to the click-through stage">
                <Check size={12} /> I&apos;m logged in
              </button>
            )}
            {(phase === "waiting_for_login" || phase === "waiting_for_navigate") && (
              <button type="button" className="np-btn flex items-center gap-1"
                      onClick={() => void act(signalSessionDone)}
                      title="Save the session and everything the browser recorded">
                <Check size={12} /> Finish &amp; capture
              </button>
            )}
            {!terminal && (
              <button type="button" className="np-btn-ghost border flex items-center gap-1"
                      style={{ borderColor: "var(--rule)" }}
                      onClick={() => void act(signalSessionCancel)}>
                <XIcon size={12} /> Cancel
              </button>
            )}
            {novnc && !terminal && (
              <a href={novnc} target="_blank" rel="noreferrer"
                 className="font-mono text-[10px] underline text-[var(--fg-faint)] ml-auto"
                 title="Open the same browser in its own tab if you need more room">
                open in a new tab
              </a>
            )}
            {phase === "done" && (
              <span className="font-mono text-[10px] text-[var(--fg-muted)]">
                Captured. Tell the agent it is done — it can now read the trace with
                {" "}<code>capture_requests</code>.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ── side panels ───────────────────────────────────────────────────── */
function StatusPanel({ harness }: { harness: string }) {
  const q = useQuery({ queryKey: ["agent/status", harness], queryFn: () => fetchAgentStatus(harness) });
  if (q.isLoading) return <Spinner label="Loading" />;
  if (q.error) return <div className="np-error font-mono text-[11px]">{explainError(q.error)}</div>;
  const s = q.data!.settings;
  const rows: Array<[string, string]> = [
    ["provider", s.provider], ["endpoint", s.api_base ?? "—"], ["model", s.model ?? "—"],
    ["api key", s.has_api_key ? "set" : "none"], ["max steps", String(s.max_steps)],
    ["repo root", s.repo_root], ["exec mode", s.exec_mode + (s.exec_mode === "ssh" ? ` → ${s.np92_alias}:${s.np92_project_dir}` : "")],
    ["approval", s.approval_policy], ["file edits", s.allow_edits ? "ENABLED" : "disabled"],
    ["sql writes", s.allow_sql_writes ? "ENABLED" : "disabled"], ["commit", s.commit_mode], ["post-edit sync", s.sync_cmd_set ? "configured" : "none"],
    ["changelog", s.changelog], ["backups", s.backup_dir], ["sessions", s.sessions_dir], ["hidden paths", String(s.hidden_path_count)],
  ];
  const byRisk = new Map<string, AgentTool[]>();
  for (const t of q.data!.tools) byRisk.set(t.risk, [...(byRisk.get(t.risk) ?? []), t]);
  return (
    <div className="flex flex-col gap-3">
      <dl className="grid grid-cols-[92px,1fr] gap-x-2 gap-y-1 font-mono text-[10px]">
        {rows.map(([k, v]) => (
          <Fragment key={k}>
            <dt className="text-[var(--fg-faint)] uppercase tracking-folio">{k}</dt>
            <dd className="text-[var(--fg)] break-all">{v}</dd>
          </Fragment>
        ))}
      </dl>
      <div>
        <div className="font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">Tools ({q.data!.tools.length})</div>
        {[...byRisk.entries()].map(([risk, tools]) => (
          <div key={risk} className="mb-1.5">
            <div className="font-mono text-[10px]" style={{ color: RISK_TONE[risk] ?? "var(--fg-muted)" }}>{risk}</div>
            <div className="flex flex-wrap gap-1">
              {tools.map((t) => (
                <span key={t.name} title={t.description} className="np-chip" style={{ background: "transparent", borderColor: "var(--rule)", color: t.enabled ? "var(--fg)" : "var(--fg-faint)", textDecoration: t.enabled ? "none" : "line-through" }}>{t.name}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ToolsPanel({ tools }: { tools: AgentTool[] }) {
  const [name, setName] = useState<string>(tools[0]?.name ?? "crawl_coverage");
  const [args, setArgs] = useState<string>("{}");
  const [out, setOut] = useState<{ ok: boolean; result: string; duration_ms: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const tool = useMemo(() => tools.find((t) => t.name === name), [tools, name]);
  const run = useMutation({
    mutationFn: async () => runTool(name, JSON.parse(args || "{}") as Record<string, unknown>),
    onSuccess: (r) => { setOut(r); setErr(null); },
    onError: (e) => setErr(explainError(e)),
  });
  useEffect(() => {
    // pre-fill args with the schema's required keys
    const props = (tool?.parameters as { properties?: Record<string, { default?: unknown }> } | undefined)?.properties ?? {};
    const req = ((tool?.parameters as { required?: string[] } | undefined)?.required) ?? [];
    const seed: Record<string, unknown> = {};
    for (const k of req) seed[k] = props[k]?.default ?? "";
    setArgs(JSON.stringify(seed, null, 2)); setOut(null); setErr(null);
  }, [tool]);
  const pretty = useMemo(() => {
    if (!out) return "";
    try { return JSON.stringify(JSON.parse(out.result), null, 2); } catch { return out.result; }
  }, [out]);
  return (
    <div className="flex flex-col gap-2">
      <select value={name} onChange={(e) => setName(e.target.value)} className="np-input font-mono text-[11px]">
        {tools.map((t) => <option key={t.name} value={t.name} disabled={!t.enabled}>{t.name} [{t.risk}]</option>)}
      </select>
      {tool && <p className="font-sans text-[11px] text-[var(--fg-muted)]">{tool.description}</p>}
      {tool && (
        <details className="font-mono text-[10px]"><summary className="cursor-pointer text-[var(--fg-faint)]">parameters schema</summary>
          <pre className="whitespace-pre-wrap text-[var(--fg-muted)] max-h-[160px] overflow-auto">{JSON.stringify(tool.parameters, null, 1)}</pre></details>
      )}
      <textarea value={args} onChange={(e) => setArgs(e.target.value)} rows={5} className="np-input font-mono text-[11px]" spellCheck={false} />
      <button className="np-btn flex items-center gap-1 self-start" disabled={run.isPending} onClick={() => run.mutate()}>
        <Play size={12} /> {run.isPending ? "running…" : "run"}
      </button>
      {tool && (tool.risk === "write" || tool.risk === "exec") && (
        <div className="font-mono text-[10px] text-[var(--accent)]">This tool changes state on the crawler host or repo. Backups + AGENT_CHANGES.md apply.</div>
      )}
      {err && <div className="np-error font-mono text-[11px]">{err}</div>}
      {out && (
        <div className="font-mono text-[10px]">
          <div className="text-[var(--fg-faint)] mb-1">{out.ok ? "ok" : "error"} · {out.duration_ms} ms · {out.result.length.toLocaleString()} chars</div>
          <pre className="whitespace-pre-wrap max-h-[50vh] overflow-auto p-2" style={{ background: "var(--bg-inset)" }}>{pretty}</pre>
        </div>
      )}
    </div>
  );
}

function ChangesPanel() {
  const q = useQuery({ queryKey: ["agent/changes"], queryFn: fetchChanges });
  if (q.isLoading) return <Spinner label="Loading" />;
  if (q.error) return <div className="np-error font-mono text-[11px]">{explainError(q.error)}</div>;
  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">AGENT_CHANGES.md</div>
        {q.data!.changelog ? (
          <pre className="font-mono text-[10px] whitespace-pre-wrap max-h-[50vh] overflow-auto p-2" style={{ background: "var(--bg-inset)" }}>{q.data!.changelog}</pre>
        ) : <div className="font-sans text-[12px] text-[var(--fg-muted)]">No changes recorded yet.</div>}
      </div>
      <div>
        <div className="font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">Backups ({q.data!.backups.length})</div>
        <ul className="font-mono text-[10px] text-[var(--fg-muted)]">
          {q.data!.backups.slice(0, 30).map((b) => <li key={b.stamp}>{b.stamp} — {b.files.join(", ")}</li>)}
        </ul>
      </div>
    </div>
  );
}

function BriefPanel({ harness }: { harness: string }) {
  const q = useQuery({ queryKey: ["agent/brief", harness], queryFn: () => fetchAgentBrief(harness) });
  if (q.isLoading) return <Spinner label="Loading" />;
  if (q.error) return <div className="np-error font-mono text-[11px]">{explainError(q.error)}</div>;
  return (
    <div>
      <div className="font-sans text-[11px] text-[var(--fg-muted)] mb-2">Exactly what the model receives as its system prompt (AGENTS.md + live environment + tool list).</div>
      <pre className="font-mono text-[10px] whitespace-pre-wrap max-h-[70vh] overflow-auto p-2" style={{ background: "var(--bg-inset)" }}>{q.data}</pre>
    </div>
  );
}
